import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Accordion,
  Badge,
  ButtonText,
  Flex,
  IllustratedMessage,
  Spinner,
  Typography,
} from '@percent/lemonade'
import { useAccordionExpansion } from '../../hooks'
import { EXPANDED_PERMISSION_AREAS_STORAGE_KEY } from '../../constants/permissions'
import { groupDomainsByArea } from '../../utils/groupDomainsByArea'
import {
  inheritedCount,
  isAssigned,
  isInherited,
} from '../../utils/domainAssignment'
import { DomainAccordion } from '../domainAccordion'
import type { AssignedDomain } from '../../types/permissions.types'
import type { PermissionsListProps } from './PermissionsList.types'
import styles from './PermissionsList.module.scss'

export function PermissionsList({
  title,
  assignedDomains,
  isLoading = false,
  isDisabled = false,
  renderSelectedRoles,
  assignAccountRole,
  unassignAccountRole,
  headerClassName,
  accordionClassName,
  showEmptyState = false,
  effectiveDomains,
  getGroupHref,
}: PermissionsListProps) {
  const { t } = useTranslation()

  // Role changes only update the toggled DomainAccordion, so keep each returned
  // domain for the area counts until the parent passes a new copy of it. The
  // copy is taken when the change resolves, matching DomainAccordion's reset.
  const [updatedDomains, setUpdatedDomains] = useState<
    Record<string, { base: AssignedDomain; updated: AssignedDomain }>
  >({})
  const currentDomains = useRef(assignedDomains)
  currentDomains.current = assignedDomains

  const trackUpdate = async (
    domainId: string,
    update: Promise<AssignedDomain>
  ) => {
    const updated = await update
    const base = currentDomains.current.find(({ id }) => id === domainId)

    if (base) {
      setUpdatedDomains((prev) => ({
        ...prev,
        [domainId]: { base, updated },
      }))
    }

    return updated
  }

  const withTrackedUpdate = (domain: AssignedDomain) => {
    const tracked = updatedDomains[domain.id]

    return tracked?.base === domain ? tracked.updated : domain
  }

  const renderAreaHeader = (area: string, domains: AssignedDomain[]) => {
    const current = domains.map(withTrackedUpdate)
    const assignedCount = current.filter(isAssigned).length
    const inheritedDomainCount = current.filter(isInherited).length

    return (
      <Flex
        justify="space-between"
        align="center"
        gap={4}
        className={styles.areaHeader}
      >
        <Typography variant="default-medium">{area}</Typography>
        <Flex align="center" gap={4}>
          {assignedCount > 0 && (
            <Badge
              variant="informative"
              shape="oval"
              size="small"
              testId={`area-${area}-assigned-count`}
            >
              {t('drawer.accounts.assignedCount', { count: assignedCount })}
            </Badge>
          )}
          {inheritedDomainCount > 0 && (
            <Badge
              variant="default"
              shape="oval"
              size="small"
              testId={`area-${area}-inherited-count`}
            >
              {inheritedCount(inheritedDomainCount, t)}
            </Badge>
          )}
        </Flex>
      </Flex>
    )
  }

  const sourcesByDomain = useMemo(
    () =>
      Object.fromEntries(
        (effectiveDomains ?? []).map((domain) => [
          domain.id,
          Object.fromEntries(
            domain.roles.map((role) => [role.id, role.sources])
          ),
        ])
      ),
    [effectiveDomains]
  )

  const areas = useMemo(
    () => groupDomainsByArea(assignedDomains),
    [assignedDomains]
  )

  const areaNames = useMemo(() => areas.map(({ area }) => area), [areas])
  const domainIds = useMemo(
    () => assignedDomains.map((domain) => domain.id),
    [assignedDomains]
  )

  const {
    expandedItems: expandedAreas,
    handleExpandAll: handleExpandAllAreas,
    handleAccordionChange: handleAreaChange,
    isAllExpanded: isEveryAreaExpanded,
  } = useAccordionExpansion(areaNames, EXPANDED_PERMISSION_AREAS_STORAGE_KEY)

  const {
    expandedItems: expandedDomains,
    handleExpandAll: handleExpandAllDomains,
    handleAccordionChange: handleDomainChange,
    isAllExpanded: isEveryDomainExpanded,
  } = useAccordionExpansion(domainIds)

  const isAllExpanded = isEveryAreaExpanded && isEveryDomainExpanded

  const handleExpandAll = () => {
    // Both levels track "expand all" independently, so drive them to the same
    // target rather than letting each toggle from its own state.
    if (isAllExpanded) {
      handleAreaChange([])
      handleDomainChange([])

      return
    }

    if (!isEveryAreaExpanded) handleExpandAllAreas()
    if (!isEveryDomainExpanded) handleExpandAllDomains()
  }

  if (isLoading) {
    return (
      <Flex justify="center" py="xl">
        <Spinner size="l" />
      </Flex>
    )
  }

  if (showEmptyState && assignedDomains.length === 0) {
    return (
      <>
        <Flex
          justify="space-between"
          align="center"
          className={headerClassName}
        >
          <Typography variant="h5">{title}</Typography>
        </Flex>
        <IllustratedMessage
          illustration="no-results"
          title={t('drawer.accounts.noRolesAvailable')}
        />
      </>
    )
  }

  return (
    <>
      <Flex justify="space-between" align="center" className={headerClassName}>
        <Typography variant="h5">{title}</Typography>
        <ButtonText onClick={handleExpandAll}>
          {isAllExpanded
            ? t('typography.collapseAll')
            : t('typography.expandAll')}
        </ButtonText>
      </Flex>

      <Accordion
        variant="default"
        value={expandedAreas}
        onChange={handleAreaChange}
        className={accordionClassName}
        multiple
      >
        {areas.map(({ area, domains }) => (
          <Accordion.Item key={area} value={area} data-testid={`area-${area}`}>
            <Accordion.Control
              className={styles.areaControl}
              data-testid={`area-${area}-control`}
            >
              {renderAreaHeader(area, domains)}
            </Accordion.Control>
            <Accordion.Panel data-testid={`area-${area}-domains`}>
              <Accordion
                variant="default"
                value={expandedDomains}
                onChange={handleDomainChange}
                className={styles.domainList}
                multiple
              >
                {domains.map((domain) => (
                  <DomainAccordion
                    key={domain.id}
                    domain={domain}
                    isDisabled={isDisabled || !domain.canSetRoles}
                    renderSelectedRoles={renderSelectedRoles}
                    roleSources={sourcesByDomain[domain.id]}
                    getGroupHref={getGroupHref}
                    assignAccountRole={async (roleId) =>
                      trackUpdate(
                        domain.id,
                        assignAccountRole(roleId, domain.id)
                      )
                    }
                    unassignAccountRole={async (roleId) =>
                      trackUpdate(
                        domain.id,
                        unassignAccountRole(roleId, domain.id)
                      )
                    }
                  />
                ))}
              </Accordion>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </>
  )
}
